import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ApiService } from './api.service';

// tslint:disable-next-line:max-line-length
import { Level, Game, LevelProgress } from '../_models/model';

@Injectable({
  providedIn: 'root'
})
export class GameService {
  private levels$: Level[] | null = null;
  private levelProgress: LevelProgress[];

  constructor(private readonly api: ApiService) {
    this.levelProgress = [];
  }

  public getLevel(levelNr: number): Level {
    if (this.levels$ === null) {
      throw Error('levels are not yet loaded');
    }
    // console.log(this.levels$);
    const level = this.levels$.find(x => x.order === levelNr);
    if (!level) {
      throw Error('level not found ' + levelNr);
    }

    return level;
  }

  /**
   * Load all levels from api
   */
  public loadLevels(): Observable<Level[]> {
    // console.log('Call API to load levels');
    if (this.levels$) {
      return of(this.levels$);
    }

    return this.api.getLevels().pipe(tap(levels => {
      this.levels$ = levels;
    }));
  }

  public isGameCompleted(): boolean {
    if (this.getLevelOpenStatus(8)) {
      return true;
    } else {
      return false;
    }
  }

  public getActiveLevel(): number {
    let lastLevelComplete = 0;
    for (let i = 1; i <= 8; i++) {
      if (this.getLevelOpenStatus(i)) {
        lastLevelComplete = i;
      }
    }
    return lastLevelComplete;
  }

  // public loadLevel(e: any) {
  //   console.log(e);
  // }

  public getColofon(): Observable<string> {
    return this.api.getColofon();
  }

  sendDiploma(email: string): Observable<Object> {
    return this.api.postDiploma(email);
  }

  /**
   * Returns current game step of a level.
   * WARNING: Returns 9 if level is completed! Handle carefully.
   * @param levelNr The level number to be checked
   */
  public getCurrentStep(levelNr: number): number {
    let getCurrentStep = 0;

    for (const levelElement of this.getLevel(levelNr).levelElements) {
      const isCompleted = this.getGameCompletedStatus(levelNr, levelElement.step);
      if (isCompleted === true) {
        getCurrentStep = Math.max(getCurrentStep, levelElement.step);
      }
    }
    return getCurrentStep + 1;
  }

  getLevelOpenStatus(levelNr: number): boolean {
    // Always allow gameId 1 since its the first game
    // tslint:disable-next-line:triple-equals

    if (levelNr === 0) {
      return true;
    }

    // Don't allow levels higher than 8;
    if (levelNr > 8) {
      return false;
    }

    if (this.levels$ === undefined) {
      console.error('checking data, but data is not yet available');
      return false;
    }

    const level = this.getLevel(levelNr);

    if (level === undefined || level === null) {
      return false;
    }
    const prevLevel = this.getLevel(levelNr - 1);

    const levelElementUuids = prevLevel.levelElements.map(x => x.uuid);
    const levelElementsProgress = this.levelProgress.filter(x => levelElementUuids.includes(x.levelElementUuid));
    if (levelElementsProgress.length === 0) {
      return false;
    }
    const completed = levelElementsProgress.every(x => x.completed === true);
    return completed;
  }

  public getGameCompletedStatus(levelNr: number, levelStep: number): boolean {
    const levelData = this.getLevel(levelNr);

    const levelElement = levelData.levelElements.find(x => x.step === levelStep);
    if (!levelElement) {
      return false;
    }

    const levelUserData = this.levelProgress.find(x => x.levelElementUuid === levelElement.uuid);
    if (!levelUserData) {
      return false
    }
    return levelUserData.completed;
  }

  public getGameOpenStatus(levelNr: number, levelStep: number): boolean {
    // Always allow gameId 1 since its the first game
    // tslint:disable-next-line:triple-equals
    if (levelStep == 1) {
      return true;
    }

    // Don't allow gameId's higher than 8;
    if (levelNr > 8) {
      return false;
    }
    const previousLevelElementUuid = this.getLevel(levelNr).levelElements.filter(x => x.step === levelStep - 1)[0].uuid;
    const previousLevelProgress = this.levelProgress.filter(x => x.levelElementUuid === previousLevelElementUuid)[0];

    // If previous game is completed, allow the current game.
    if (previousLevelProgress.completed) {
      return true;
    }
    return false;
  }

  public getLevelProgress(levelNr: number): LevelProgress[] {
    // Find all levelElementUuids from a certain level.
    // tslint:disable-next-line:triple-equals
    const levelElementUuids = this.getLevel(levelNr).levelElements.map(x => x.uuid);
    // Get all levelProgresses from that level.
    return this.levelProgress.filter(x => levelElementUuids.includes(x.levelElementUuid));
  }

  loadProgressFromServer(): Observable<LevelProgress[]> {
    // console.warn('loadProgressFromServer called');
    return this.api.getLevelProgress()
      .pipe(
        map(response => {
          this.levelProgress = response.map(levelProgress => {
            return {
              ...levelProgress,
              // completed: true
            }
          });
          return this.levelProgress;
        }))
    // .pipe(tap(received => {

    //   console.log('progress', this.levelProgress);
    //   return received;
    // }));
  }

  public userDataExists(): boolean {
    if (!this.levelProgress) {
      return false;
    } else if (this.levelProgress.length === 0) {
      return false;
    }
    return true;
  }

  public setLevelProgress(levelElementUuid: string, data: string, completed: boolean) {
    const levelProgress = this.levelProgress.filter(x => x.levelElementUuid === levelElementUuid)[0];
    if (levelProgress) {
      levelProgress.data = data;
      levelProgress.completed = completed;
      this.sendLevelProgress(levelProgress).subscribe();
    } else {
      // this.levelProgress
    }

  }

  private sendLevelProgress(levelProgress: LevelProgress): Observable<any> {
    return this.api.sendLevelProgress(levelProgress);
  }

  /**
   *
   * @param levelNr 1 tm 8
   * @param gameNr 1 tm x
   */
  public getGame(levelNr: number, gameNr: number): Game {
    // console.log('get game', levelNr, gameNr)
    // Triple-equals doesn't work here for some reason
    // tslint:disable-next-line:triple-equals
    const levelElement = this.getLevel(levelNr).levelElements.find(x => x.step == gameNr);
    if (!levelElement) {
      console.error('get game', levelNr, gameNr, 'not found');
      throw Error('get game ' + levelNr + ' ' + gameNr + ' not found');
    }
    const game = levelElement.game;
    game.levelElement = levelElement;   // register the levelement in the game
    return game;
  }



  getActiveLevels() {
    // Level nr 1 is always active
    const activeLevels = [0];
    // Check level 2 to 8 for active status
    for (let i = 1; i <= 8; i++) {
      // Push to active array is active
      if (this.getLevelOpenStatus(i)) {
        activeLevels.push(i);
      } else {
        // Cancel loop if found inactive level.
        break;
      }
    }
    return activeLevels;
  }
}
